import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/ui/avatar';
import { Search, MessageSquare, FileText, Users } from 'lucide-react';
import { format } from 'date-fns';

interface Client {
  client_id: string;
  first_name: string;
  last_name: string;
  email: string;
  total_sessions: number;
  last_session_at?: string;
  next_session_at?: string;
  total_spent: number;
}

export const ClientsTab = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClients();
  }, [user]);

  const loadClients = async () => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('sessions')
        .select(`
          client_id,
          price,
          scheduled_at,
          status,
          profiles!sessions_client_id_fkey (
            first_name,
            last_name,
            email
          )
        `)
        .eq('therapist_id', user.id);

      if (error) throw error;

      // Process and aggregate client data
      const clientMap = new Map<string, Client>();

      data?.forEach(session => {
        const clientId = session.client_id;
        if (!clientMap.has(clientId)) {
          clientMap.set(clientId, {
            client_id: clientId,
            first_name: session.profiles?.first_name || '',
            last_name: session.profiles?.last_name || '',
            email: session.profiles?.email || '',
            total_sessions: 0,
            total_spent: 0
          });
        }

        const client = clientMap.get(clientId)!;
        
        if (session.status === 'completed') {
          client.total_sessions++;
          client.total_spent += parseFloat(String(session.price));
        }

        const sessionDate = new Date(session.scheduled_at);
        const now = new Date();

        if (session.status === 'scheduled') {
          if (sessionDate > now) {
            if (!client.next_session_at || sessionDate < new Date(client.next_session_at)) {
              client.next_session_at = session.scheduled_at;
            }
          }
        }

        if (session.status === 'completed') {
          if (!client.last_session_at || sessionDate > new Date(client.last_session_at)) {
            client.last_session_at = session.scheduled_at;
          }
        }
      });

      const clientList = Array.from(clientMap.values());
      setClients(clientList);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    `${client.first_name} ${client.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Card><CardContent className="p-8 text-center">Loading clients...</CardContent></Card>;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <CardTitle className="flex items-center">
              <Users className="h-5 w-5 mr-2" />
              My Clients
            </CardTitle>
            <CardDescription>
              Manage your client relationships
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search clients by name or email..."
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="rounded-md border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Client</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Sessions</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Total Spent</th>
                <th className="text-left p-3 font-medium hidden md:table-cell">Next Session</th>
                <th className="text-left p-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => (
                  <tr key={client.client_id} className="border-t hover:bg-muted/30">
                    <td className="p-3">
                      <div className="flex items-center">
                        <Avatar className="h-8 w-8 mr-3">
                          <div className="h-full w-full flex items-center justify-center bg-primary-foreground text-primary font-medium">
                            {client.first_name.charAt(0)}
                          </div>
                        </Avatar>
                        <div>
                          <div className="font-medium">
                            {client.first_name} {client.last_name}
                          </div>
                          <div className="text-xs text-muted-foreground">{client.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 hidden md:table-cell">{client.total_sessions}</td>
                    <td className="p-3 hidden md:table-cell">
                      ${client.total_spent.toFixed(2)}
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      {client.next_session_at ? (
                        format(new Date(client.next_session_at), 'MMM d, yyyy')
                      ) : (
                        <span className="text-muted-foreground">None scheduled</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="icon" title="View details">
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send message">
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-muted-foreground">
                    No clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
